import React from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOMeta, { PAGE_META } from '../../components/SEOMeta';
import { TCLIconImage } from '../../components/TCLMark';
import FourPillarsGrid from '../../components/FourPillarsGrid';

const CONSENT_ITEMS = [
  {
    title: 'This is a reflective tool, not a psychometric test.',
    desc: 'The CFQ helps you notice patterns in how you navigate cultural difference. It does not measure intelligence, competence, or personality.',
  },
  {
    title: 'Your answers stay on your device.',
    desc: 'Scoring happens in your browser. We do not store your responses or link them to your identity. You may optionally share anonymous band-level data after seeing your results.',
  },
  {
    title: 'Not for hiring, selection, or evaluation.',
    desc: 'The CFQ is designed for personal reflection and facilitated development. It should not be used to assess candidates or rank individuals.',
  },
  {
    title: '20 questions across four pillars.',
    desc: 'Self-Anchoring, Perspective-Shifting, Adaptive Communication, and Relational Integration. Takes approximately 10 minutes.',
  },
];

const CFQConsentScreen = ({ sessionCode, onConsent }) => (
  <div className="min-h-screen bg-[#FAF8F3]">
    <SEOMeta url="/cfq-assessment" {...PAGE_META.cfqAssessment} />

    {/* ── Dark header — brand intro ── */}
    <div className="relative overflow-hidden" style={{ backgroundColor: '#071B3A' }}>
      <div className="absolute top-0 right-0 w-[50%] h-full bg-gradient-to-l from-[#C99316]/[0.03] to-transparent pointer-events-none" />
      <div className="relative z-10 pt-28 pb-16 md:pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          <span className="block text-[11px] tracking-[0.3em] uppercase font-medium mb-6" style={{ color: 'rgba(201,147,22,0.7)' }}>
            Before You Begin
          </span>
          <TCLIconImage size={56} className="mb-5" />
          <h1
            className="font-serif text-[36px] md:text-[44px] font-light tracking-tight leading-[1.1] mb-3"
            style={{ color: 'rgba(250,248,243,0.95)' }}
            data-testid="consent-title"
          >
            Cultural Fluency Quotient
          </h1>
          <p className="font-sans text-base font-light" style={{ color: 'rgba(250,248,243,0.4)' }}>
            CFQ Self-Assessment · Promise Monday
          </p>
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(201,147,22,0.2) 30%, rgba(201,147,22,0.2) 70%, transparent)' }}
      />
    </div>

    {/* ── Ivory body — consent & pillars ── */}
    <div className="py-14 px-6">
      <div className="max-w-2xl mx-auto">

        {/* Consent items */}
        <div className="space-y-4 mb-10">
          {CONSENT_ITEMS.map((item, i) => (
            <div
              key={i}
              className="flex gap-4 items-start bg-white border border-navy/[0.07] rounded-xl p-5 shadow-sm shadow-navy/[0.03]"
            >
              <div
                className="w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ borderColor: 'rgba(201,147,22,0.35)' }}
              >
                <Check size={12} style={{ color: 'rgba(201,147,22,0.7)' }} />
              </div>
              <div>
                <p className="text-navy font-semibold text-[16px] mb-1.5">{item.title}</p>
                <p className="text-navy/50 text-[16px] leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Session code banner */}
        {sessionCode && (
          <div
            className="border rounded-lg px-5 py-3 mb-8"
            style={{ backgroundColor: 'rgba(7,27,58,0.04)', borderColor: 'rgba(201,147,22,0.2)' }}
          >
            <p className="text-navy/55 text-[16px]">
              Workshop session:{' '}
              <span className="font-medium" style={{ color: '#C99316' }}>{sessionCode}</span>
            </p>
          </div>
        )}

        {/* Four-pillar preview */}
        <div className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.25em] font-sans font-medium mb-5" style={{ color: 'rgba(201,147,22,0.65)' }}>
            The Four Pillars
          </p>
          <FourPillarsGrid compact />
        </div>

        {/* Begin button */}
        <button
          onClick={onConsent}
          data-testid="consent-begin-btn"
          className="inline-flex items-center gap-3 px-9 py-4 text-[17px] font-semibold uppercase tracking-[0.08em] transition-all hover:brightness-110 shadow-lg"
          style={{ backgroundColor: '#C99316', color: '#071B3A' }}
        >
          I understand — begin <ChevronRight size={18} />
        </button>

        <p className="text-navy/30 text-[13px] mt-6">
          By continuing you confirm you have read and understood the above.{' '}
          <Link to="/privacy" className="hover:text-gold underline" style={{ color: 'rgba(201,147,22,0.5)' }}>
            Privacy policy
          </Link>
        </p>

        {/* ── Facilitator note ── */}
        <div
          className="mt-12 pt-8"
          style={{ borderTop: '1px solid rgba(201,147,22,0.15)' }}
          data-testid="cfq-facilitator-note"
        >
          <p className="text-xs uppercase tracking-[0.22em] font-medium mb-2" style={{ color: 'rgba(201,147,22,0.55)' }}>
            Facilitating a live event?
          </p>
          <p className="text-[16px] font-light leading-relaxed mb-4" style={{ color: 'rgba(7,27,58,0.5)' }}>
            Running a workshop, conference session, or team event? Request a session code so your audience takes the CFQ together — you see each participant's band results as they finish, displayed live on screen.
          </p>
          <Link
            to="/facilitate/login"
            className="inline-flex items-center gap-1.5 text-[16px] font-medium transition-colors hover:opacity-80"
            style={{ color: 'rgba(201,147,22,0.85)' }}
            data-testid="cfq-facilitator-portal-link"
          >
            Request a session code <ChevronRight size={15} />
          </Link>
        </div>

      </div>
    </div>
  </div>
);

export default CFQConsentScreen;
